import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin } from "lucide-react";
import api from "@/lib/api";
import { BRAND_LOGO } from "@/lib/assets";

/**
 * Public legal pages: privacy, terms, contact.
 * Route: /c/:slug/:doc  OR  /:doc (uses default campaign)
 * doc ∈ {privacy, terms, contact}
 */
const DOC_META = {
  privacy: {
    title: "מדיניות פרטיות",
    field: "privacy_policy",
    empty: "מדיניות הפרטיות טרם הוגדרה. עדכנו אותה דרך פאנל הניהול.",
  },
  terms: {
    title: "תקנון",
    field: "terms",
    empty: "התקנון טרם הוגדר. עדכנו אותו דרך פאנל הניהול.",
  },
  contact: {
    title: "יצירת קשר",
    field: null,
    empty: "פרטי יצירת קשר טרם הוגדרו.",
  },
};

export default function LegalPage({ docType }) {
  const params = useParams();
  const navigate = useNavigate();
  const slug = params.slug || null;
  const doc = docType || params.doc;
  const meta = DOC_META[doc];

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const url = slug
          ? `/campaigns/public/default?slug=${encodeURIComponent(slug)}`
          : "/campaigns/public/default";
        const { data } = await api.get(url);
        setCampaign(data);
      } catch (err) {
        console.error("Legal page: campaign load failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (!meta) {
    navigate("/");
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="w-10 h-10 rounded-full border-4 border-brand-amber border-t-transparent animate-spin" />
      </div>
    );
  }

  const backHref = slug ? `/c/${slug}` : "/";

  return (
    <div className="min-h-screen bg-[#fafaf9] text-brand-charcoal" data-testid="legal-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-black/5">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to={backHref} className="flex items-center gap-1 text-sm font-bold text-stone-600 hover:text-brand-charcoal" data-testid="legal-back-link">
            <ArrowLeft className="w-4 h-4 rotate-180" />
            חזרה
          </Link>
          <img src={BRAND_LOGO} alt="" className="h-9" />
          <span className="w-12" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <h1 className="text-3xl sm:text-5xl font-[800] mb-3" data-testid="legal-title">{meta.title}</h1>
        <div className="text-stone-500 mb-10">
          {campaign?.brand_name || "אמת או חובות"}
        </div>

        {doc === "contact" ? (
          <ContactContent campaign={campaign} />
        ) : (
          <DocumentContent campaign={campaign} fieldName={meta.field} empty={meta.empty} />
        )}

        <div className="mt-16 flex gap-2 flex-wrap text-sm">
          <FooterPill to={`${slug ? `/c/${slug}` : ""}/privacy`} active={doc === "privacy"}>מדיניות פרטיות</FooterPill>
          <FooterPill to={`${slug ? `/c/${slug}` : ""}/terms`} active={doc === "terms"}>תקנון</FooterPill>
          <FooterPill to={`${slug ? `/c/${slug}` : ""}/contact`} active={doc === "contact"}>יצירת קשר</FooterPill>
        </div>
      </main>

      <footer className="bg-brand-stone text-stone-300 mt-12">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 text-sm text-center">
          © 2026 {campaign?.brand_name || "אמת או חובות"}. כל הזכויות שמורות.
        </div>
      </footer>
    </div>
  );
}

function FooterPill({ to, active, children }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-full text-sm font-bold border transition ${
        active
          ? "bg-brand-charcoal text-white border-brand-charcoal"
          : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
      }`}
    >
      {children}
    </Link>
  );
}

function DocumentContent({ campaign, fieldName, empty }) {
  const text = (campaign && campaign[fieldName]) || "";
  if (!text.trim()) {
    return (
      <div className="bg-white rounded-2xl p-8 border border-black/5 text-center text-stone-500">{empty}</div>
    );
  }
  return (
    <article className="bg-white rounded-2xl p-6 md:p-10 border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="legal-content">
      <RichText text={text} />
    </article>
  );
}

/**
 * Render plain-text content with simple heuristics:
 * - Lines starting with "1." "2." etc → h3
 * - Lines starting with "•" → bullet list item
 * - Other lines → paragraph
 * - Empty lines → spacing
 */
function RichText({ text }) {
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let bulletGroup = null;

  const flushBullets = () => {
    if (bulletGroup) {
      blocks.push({ type: "ul", items: bulletGroup });
      bulletGroup = null;
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      blocks.push({ type: "space", key: idx });
      return;
    }
    if (/^\d+\.\s/.test(line)) {
      flushBullets();
      blocks.push({ type: "h3", text: line, key: idx });
      return;
    }
    if (/^[•·\-*]\s/.test(line)) {
      bulletGroup = bulletGroup || [];
      bulletGroup.push(line.replace(/^[•·\-*]\s/, ""));
      return;
    }
    flushBullets();
    blocks.push({ type: "p", text: line, key: idx });
  });
  flushBullets();

  return (
    <div className="space-y-3 leading-[1.85] text-stone-800">
      {blocks.map((b, i) => {
        if (b.type === "space") return <div key={`s-${i}`} className="h-3" />;
        if (b.type === "h3") {
          return (
            <h3 key={`h-${i}`} className="text-xl md:text-2xl font-[800] mt-6 text-brand-charcoal">
              {b.text}
            </h3>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={`u-${i}`} className="space-y-2 ps-5 list-disc marker:text-brand-amber">
              {b.items.map((it, j) => (
                <li key={`${i}-${j}`}><InlineText text={it} /></li>
              ))}
            </ul>
          );
        }
        return <p key={`p-${i}`}><InlineText text={b.text} /></p>;
      })}
    </div>
  );
}

/**
 * Safely render plain text with **bold** markers as React nodes.
 * Splits on `**...**` and wraps matches in <strong>. No HTML parsing → no XSS.
 */
function InlineText({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function ContactContent({ campaign }) {
  const email = campaign?.contact_email || "";
  const phone = campaign?.contact_phone || "";
  const address = campaign?.contact_address || "";

  if (!email && !phone && !address) {
    return (
      <div className="bg-white rounded-2xl p-8 border border-black/5 text-center text-stone-500">
        פרטי יצירת קשר טרם הוגדרו. עדכנו אותם דרך פאנל הניהול.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 md:p-10 border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)] space-y-5" data-testid="contact-content">
      <p className="text-stone-600 leading-[1.7] mb-6">
        אנחנו כאן בשבילכם. השאירו פרטים או צרו קשר ישיר באחת מהדרכים הבאות:
      </p>

      {email && (
        <ContactRow icon={Mail} label="אימייל" href={`mailto:${email}`} value={email} dir="ltr" />
      )}
      {phone && (
        <ContactRow icon={Phone} label="טלפון" href={`tel:${phone}`} value={phone} dir="ltr" />
      )}
      {address && (
        <ContactRow icon={MapPin} label="כתובת" value={address} />
      )}
    </div>
  );
}

function ContactRow({ icon: Icon, label, href, value, dir }) {
  const content = (
    <div className="flex items-center gap-4 p-4 rounded-2xl bg-stone-50 hover:bg-stone-100 transition border border-stone-200">
      <div className="w-12 h-12 rounded-full bg-brand-amber/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-brand-amber" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-stone-500 mb-0.5">{label}</div>
        <div className={`font-bold text-stone-800 break-all ${dir === "ltr" ? "ltr text-left" : ""}`}>{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
}
